import type { MergedSequencerNodeSchedulingInfo } from '../../../models/audioEditor/types/SequencerRenderingTypes';

/**
 * MergedNodeHelpers
 *
 * Shared utilities for processing merged sequencer nodes.
 * Used by both browser playback (SequencerRendererImpl.js) and cloud rendering (TimeloomRenderingInputBuilder.ts).
 *
 * This module consolidates duplicated logic between the two rendering paths to ensure
 * consistent behavior and easier maintenance.
 */

/**
 * Timing information extracted from a merged node with frame-accurate snapping
 */
export interface NodeTimingInfo {
    /** Relative start time in seconds from first node (frame-snapped if enabled) */
    relativeStartTime: number;

    /** Duration in seconds (frame-snapped if enabled) */
    duration: number;

    /** Video offset in seconds, adjusted for crossfade (frame-snapped if enabled) */
    videoOffset: number;

    /** Video buffer duration in seconds (frame-snapped if enabled) */
    videoBufferDuration: number;
}

/**
 * Media URL resolution priority for different rendering contexts
 *
 * - 'blob-first': Prefer cached blob URLs (faster for browser playback)
 * - 'original-first': Prefer original URLs (required for Lambda cloud rendering)
 */
export type MediaUrlPriority = 'blob-first' | 'original-first';

/**
 * Snap a time value to the START of the nearest frame boundary using floor
 *
 * This ensures adjacent scenes touch without gaps. When Scene A ends at time T,
 * Scene B starts at floor(T * fps) / fps, creating a 1-frame overlap instead of a gap.
 *
 * @param timeInSeconds - Time value to snap
 * @param fps - Frame rate (frames per second)
 * @returns Time snapped to frame start boundary
 *
 * @example
 * // At 25fps: 1.879s → frame 46.975 → floor to frame 46 → 1.84s
 * snapToFrameStart(1.879, 25); // Returns 1.84
 */
export function snapToFrameStart(timeInSeconds: number, fps: number): number {
    if (!isFinite(fps) || fps <= 0) {
        throw new RangeError(`fps must be a finite positive number, got: ${fps}`);
    }
    if (!isFinite(timeInSeconds)) {
        throw new RangeError(`timeInSeconds must be a finite number, got: ${timeInSeconds}`);
    }
    const frameNumber = Math.floor(timeInSeconds * fps);
    return frameNumber / fps;
}

/**
 * Snap a duration value to frame boundary using ceil
 *
 * This ensures full content coverage. A duration of 1.879s at 25fps becomes 47 frames (1.88s)
 * instead of 46 frames, preventing the last frame from being cut off.
 *
 * @param timeInSeconds - Duration to snap
 * @param fps - Frame rate (frames per second)
 * @returns Duration snapped to frame boundary
 *
 * @example
 * // At 25fps: 1.879s → frame 46.975 → ceil to 47 frames → 1.88s
 * snapToFrameDuration(1.879, 25); // Returns 1.88
 */
export function snapToFrameDuration(timeInSeconds: number, fps: number): number {
    if (!isFinite(fps) || fps <= 0) {
        throw new RangeError(`fps must be a finite positive number, got: ${fps}`);
    }
    if (!isFinite(timeInSeconds)) {
        throw new RangeError(`timeInSeconds must be a finite number, got: ${timeInSeconds}`);
    }
    const frameNumber = Math.ceil(timeInSeconds * fps);
    return frameNumber / fps;
}

/**
 * Check if a merged node contains video content
 *
 * @param node - MergedSequencerNodeSchedulingInfo instance
 * @returns true if node has video, false otherwise
 *
 * @example
 * const videoNodes = mergedNodes.filter(nodeHasVideo);
 */
export function nodeHasVideo(node: MergedSequencerNodeSchedulingInfo): boolean {
    // Try convenience method first (cloud rendering path)
    if (node.isSequencerNodeReferenceHasVideo) {
        return node.isSequencerNodeReferenceHasVideo();
    }

    // Fallback to full chain (browser playback path)
    const sequencerNode = node.getSequencerNodeReference?.();
    const audioSegment = sequencerNode?.getAudioSegment?.();
    return audioSegment?.getHasVideo?.() === true;
}

/**
 * Get media URL from a merged node with configurable priority
 *
 * Different rendering contexts have different URL preferences:
 * - Browser playback: Prefers blob URLs (already in memory, faster)
 * - Lambda rendering: Requires original URLs (can't access blob URLs)
 *
 * @param node - MergedSequencerNodeSchedulingInfo instance
 * @param priority - Which URL to prefer ('blob-first' or 'original-first')
 * @returns Media URL or empty string if not found
 *
 * @example
 * // For browser playback (prefer cached blob)
 * const url = getNodeMediaUrl(node, 'blob-first');
 *
 * // For cloud rendering (need original URL)
 * const url = getNodeMediaUrl(node, 'original-first');
 */
export function getNodeMediaUrl(
    node: MergedSequencerNodeSchedulingInfo,
    priority: MediaUrlPriority = 'blob-first'
): string {
    const blobUrl = node.getCachedBlobUrl?.() || '';
    const originalUrl = node.getAudioSegmentMediaUrl?.() || '';

    if (priority === 'blob-first') {
        return blobUrl || originalUrl;
    } else {
        return originalUrl || blobUrl;
    }
}

/**
 * Extract comprehensive timing information from a merged sequencer node
 *
 * This consolidates the timing calculation logic used by both browser playback
 * and cloud rendering paths. It handles:
 * - Relative positioning (subtract first node's start time)
 * - Crossfade adjustment for video offset
 * - Duration selection (actual vs buffer duration)
 * - Optional frame-accurate snapping
 *
 * @param node - MergedSequencerNodeSchedulingInfo instance
 * @param firstNodeStartTimeMs - Start time of first node in milliseconds
 * @param fps - Frame rate for snapping
 * @param applyFrameSnapping - Whether to snap to frame boundaries (default: true)
 * @returns Timing information object
 *
 * @example
 * const timing = extractNodeTiming(node, firstNodeStartMs, 25, true);
 * const scene = new SceneItemImpl({
 *   startAt: timing.relativeStartTime,
 *   duration: timing.duration,
 *   inputs: [{
 *     offset: timing.videoOffset,
 *     duration: timing.videoBufferDuration
 *   }]
 * });
 */
export function extractNodeTiming(
    node: MergedSequencerNodeSchedulingInfo,
    firstNodeStartTimeMs: number,
    fps: number,
    applyFrameSnapping: boolean = true
): NodeTimingInfo {
    // Validate firstNodeStartTimeMs input
    if (!Number.isFinite(firstNodeStartTimeMs)) {
        console.error('[MergedNodeHelpers] Invalid firstNodeStartTimeMs:', firstNodeStartTimeMs);
        throw new Error(`Invalid firstNodeStartTimeMs: must be a finite number, got ${firstNodeStartTimeMs}`);
    }

    // Get timing in milliseconds
    const startTimeMs = node.getStartTimeOffset();
    const durationMs = node.getDuration();
    const audioBufferPlaybackDurationMs = node.getAudioBufferPlaybackDuration?.() || 0;
    const sourceStartMs = node.getStartTimeOffsetForSourceBuffer?.() || 0;
    const sourceEndMs = node.getEndTimeOffsetForSourceBuffer?.() || 0;
    const sourceLoopMs = node.getStartTimeOffsetLoopForSourceBuffer?.() || 0;
    const relativeStartTimeMs = node.getRelativeStartTime?.() || 0;

    // Calculate raw timing in seconds (before frame snapping)

    // 1. Relative start time (subtract first node's start for timeline positioning)
    const rawRelativeStartTime = (startTimeMs - firstNodeStartTimeMs) / 1000;

    // 2. Duration (use actual duration vs playback duration, whichever is smaller)
    const rawDuration = durationMs < audioBufferPlaybackDurationMs
        ? durationMs / 1000
        : audioBufferPlaybackDurationMs / 1000;

    // 3. Video offset (crossfade-adjusted)
    // CRITICAL: Crossfades are AUDIO-ONLY - video offset must subtract crossfade time
    // This matches SequencerRendererImpl.js:913 and TimeloomRenderingInputBuilder.ts:68
    const offsetBufferNode = (sourceStartMs + sourceLoopMs) / 1000;
    const rawVideoOffset = offsetBufferNode - (relativeStartTimeMs / 1000);

    // 4. Video buffer duration (from source start to source end)
    // Clamp to prevent negative duration if sourceEndMs < sourceStartMs
    const rawVideoBufferDuration = Math.max(0, (sourceEndMs - sourceStartMs) / 1000);

    // Apply frame snapping if requested
    if (applyFrameSnapping) {
        const snappedTiming = {
            relativeStartTime: snapToFrameStart(rawRelativeStartTime, fps),
            duration: snapToFrameDuration(rawDuration, fps),
            videoOffset: snapToFrameStart(rawVideoOffset, fps),
            videoBufferDuration: snapToFrameDuration(rawVideoBufferDuration, fps)
        };

        return snappedTiming;
    } else {
        return {
            relativeStartTime: rawRelativeStartTime,
            duration: rawDuration,
            videoOffset: rawVideoOffset,
            videoBufferDuration: rawVideoBufferDuration
        };
    }
}
